import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {BehaviorSubject, catchError, map, Observable, of, Subject, tap} from 'rxjs';
import { environment } from '../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { AssignUserToAgency } from '../models/AssignUserToAgency';

@Injectable({
  providedIn: 'root',
})
export class EstablishmentService {
  private cachedEstablishmentsSubject = new BehaviorSubject<any[] | null>(null);
  establishments$ = this.cachedEstablishmentsSubject.asObservable();

  constructor(private http: HttpClient, private cookie: CookieService) {}

  private refreshSubject = new Subject<void>();

    // Call this to trigger a refresh
    triggerRefresh() {
      this.refreshSubject.next();
    }
    // Subscribe to this in components that need updates
    getRefreshObservable(): Observable<void> {
      return this.refreshSubject.asObservable();
    }

  getAllEstablishments(): Observable<any> {
    if (this.cachedEstablishmentsSubject.value) {
      return of(this.cachedEstablishmentsSubject.value);
    }
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get<any[]>(
      `${environment.api.BASE_URL}/api/etablissements-without-mission`,
      { headers }
    ).pipe(
      tap((data) => {
        this.cachedEstablishmentsSubject.next(data);
      }),
      catchError((error) => {
        console.error('Failed to fetch establishments', error);
        throw error;
      })
    );
  }
  
  getAgencyEstablishments(): Observable<any> {
    if (this.cachedEstablishmentsSubject.value) {
      return of(this.cachedEstablishmentsSubject.value);
    }

    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get<any[]>(
      `${environment.api.BASE_URL}/api/etablissements/agency`,
      { headers }
    ).pipe(
      tap((data) => {
        this.cachedEstablishmentsSubject.next(data);
      }),
      catchError((error) => {
        console.error('Failed to fetch agency establishments', error);
        throw error;
      })
    );
  }

  getAgencyEstablishmentsWithPagination(offset: number, size: number, search: string = ''): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    let url = `${environment.api.BASE_URL}/api/etablissements/agency/paginated?offset=${offset}&size=${size}`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    return this.http.get<any>(url, { headers }).pipe(
      catchError(error => {
        console.error('Error fetching paginated agency establishments:', error);
        return of({ data: [], total: 0, totalPages: 0, pageNumber: 0, pageSize: size, hasNext: false, hasPrevious: false });
      })
    );
  }

  clearCache(): void {
    this.cachedEstablishmentsSubject.next(null);
  }

  updateEstablishment(body: any, id: number): Observable<any> {
     const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.put(
      `${environment.api.BASE_URL}/api/etablissements/${id}`,
      body,
      { headers }
    );
  }

  getEstablishmentById(id: any): Observable<any> {
     const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(`${environment.api.BASE_URL}/api/etablissements/${id}`,
      { headers }
    );
  }

  getEstablishmentNameById(id: any): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(`${environment.api.BASE_URL}/api/etablissements/${id}/name`, { headers }).pipe(
      catchError(error => {
        console.error('Error fetching establishment name:', error);
        return of({ id: id, name: 'Unknown' });
      })
    );
  }

    getEstablishmentByIdV2(id: any): Observable<any> {
     const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(`${environment.api.BASE_URL}/api/v2/etablissements/${id}`,
      { headers }
    );
  }

  getAgencyByEmail(email: any): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    console.log('Headers:', headers);
    return this.http.get(`${environment.api.BASE_URL}/agencies?email=${email}`,
      { headers }
    );
  }

  getEstablishmentStats(id: any): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(
      `${environment.api.BASE_URL}/api/etablissements/stat/${id}`,
      { headers }
    );
  }

  getAgencies(): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(`${environment.api.BASE_URL}/agencies`,
      { headers });
  }

  getAgencyById(id:number): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(`${environment.api.BASE_URL}/agencies/${id}`,
      { headers });
  }

  getUserById(id:number): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(`${environment.api.BASE_URL}/users/${id}`,
      { headers });
  }

  getAgenciesUsers(): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(`${environment.api.BASE_URL}/users`,
      { headers });
  }

  addAgency(body: any): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.post(`${environment.api.BASE_URL}/agencies`,
      body,
      { headers });
  }

  addAgencyUser(body: any): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.post(`${environment.api.BASE_URL}/auth`, body,
      { headers });
  }

  updateAgency(body: any, id: number): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.put(`${environment.api.BASE_URL}/agencies/${id}`, body,
      { headers });
  }

  assignDirigeanttoAgencyByAgencyId(body: AssignUserToAgency, id: number): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.put(`${environment.api.BASE_URL}/agencies/${id}`, body,
      { headers });
  }

  updateAgencyUser(body: any, id: number): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.put(`${environment.api.BASE_URL}/users/${id}`, body,
      { headers });
  }

  deleteAgency(id: number): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.delete(`${environment.api.BASE_URL}/agencies/${id}`,
      { headers, responseType: 'text' }
    );
  }

  deleteAgencyUser(id: number): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.delete(`${environment.api.BASE_URL}/users/${id}`,
      { headers }
    );
  }

  getSitesByEstablishmentId(id: any): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(
      `${environment.api.BASE_URL}/api/etablissements/${id}/sites`,
      { headers }
    );
  }
  getRegions(): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(`${environment.api.BASE_URL}/api/missions/regions`,
      { headers }
    );
  }
  getAddress(search: string, country?: string): Observable<any> {
    // Photon (photon.komoot.io) is built for autocomplete (prefix matching), unlike
    // Nominatim which needs near-complete terms (e.g. "GENEV" returned nothing, "GENEVE" did).
    // Normalized to the Nominatim-like shape so every caller (getAdresseFormated /
    // addressSelected) keeps working unchanged. Biased to France; callers still filter FR.
    const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(search)}&lang=fr&limit=10&countrycode=FR`;
    return this.http.get<any>(url).pipe(
      map((res: any) => (res?.features || [])
        .filter((f: any) => this.isPreciseAddress(f))
        .map((f: any) => this.mapPhotonFeature(f)))
    );
  }

  // Keep any result that has a single real postcode — houses, streets AND communes all pass.
  // Drops only noise: regions/states/countries (no postcode) and multi-postcode district
  // blobs (e.g. Paris "75000;75001;…").
  private isPreciseAddress(f: any): boolean {
    const pc = f?.properties?.postcode;
    return !!pc && !pc.includes(';');
  }

  private mapPhotonFeature(f: any): any {
    const p = f?.properties || {};
    const coords = f?.geometry?.coordinates || [];
    const city = p.city || p.town || p.village || '';
    const postcode = (p.postcode || '').split(';')[0].trim();
    return {
      lat: coords[1],
      lon: coords[0],
      display_name: [p.housenumber, p.street || p.name, postcode, city, p.country]
        .filter(Boolean)
        .join(' '),
      address: {
        house_number: p.housenumber || '',
        road: p.street || p.name || '',
        postcode,
        city,
        town: p.town || '',
        village: p.village || '',
        county: p.county || '',
        state: p.state || '',
        country: p.country || '',
        country_code: (p.countrycode || '').toLowerCase(),
      },
    };
  }
  getRegionByPostalCode(postalCode: string): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(
      `${environment.api.COMMUNES_URL}?codePostal=${postalCode}`,
      { headers }
    );
  }
  getRegionByCode(code: string): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(`${environment.api.REGIONS_URL}/${code}`,
      { headers }
    );
  }
  generateContract(
    candidateId: number,
    missionId: number,
    establishmentId: number
  ): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(
      `${environment.api.BASE_URL}/api/generate/save-contract-in-docsign?idCandidat=${candidateId}&idMission=${missionId}&idEtablissement=${establishmentId}`,
      { headers, responseType: 'text' }
    );
  }

  generateContractDisposition(
    candidateId: number,
    missionId: number,
    establishmentId: number
  ): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(
      `${environment.api.BASE_URL}/api/generate/contract-disposition?idCandidat=${candidateId}&idMission=${missionId}&idEtablissement=${establishmentId}`,
      { headers }
    );
  }

  generateContractPreview(
    candidateId: number,
    missionId: number,
    establishmentId: number
  ): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(
      `${environment.api.BASE_URL}/api/generate/sign-url?candidatId=${candidateId}&missionId=${missionId}&preview=${establishmentId}`,
      { headers, responseType: 'text' }
    );
  }

  getPreviewFragmentUrl(
    candidateId: number,
    establishmentId: number,
    missionFragmentId: number
  ): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(
      `${environment.api.BASE_URL}/api/generate/preview-fragment-url?candidateId=${candidateId}&establishmentId=${establishmentId}&missionFragmentId=${missionFragmentId}`,
      { headers, responseType: 'text' }
    );
  }

  getSiteByEstablishmentId(id: number, isVisible?: boolean): Observable<any> {
    const token = this.cookie.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get(
      `${environment.api.BASE_URL}/api/etablissements/${id}/sites?isVisible=${isVisible}`
      , { headers }
    );
  }

  get establishment(): any {
    try {
      return JSON.parse(this.cookie.get('establishment'));
    } catch {
      return null;
    }
  }
}
//back.dev.collary.fr/api/generate/save-contract-in-docsign?idCandidat=22&idMission=34&idEtablissement=33
